import React, { useRef, useState, useEffect } from "react";
import styles from "./NoteField.module.scss";
import OccupiedField from "../OccupiedField/OccupiedField";
import { recordSentry } from "utils/recordSentry";
import { FieldWrapper, FieldActionWrapper, NoteFieldTextArea, ConfirmationModal, Colors, ToastVariant } from "@factorearth/component-library";
import { useErrorBoundary } from "react-error-boundary";
import { FieldInstance, FieldTemplate } from "db/definitions";

interface NoteFieldProps {
	color: Colors;
	fieldInstance: FieldInstance;
	fieldTemplate: FieldTemplate;
	value: string;
	handleChange: (event: { target: { value: string } }) => void;
	handleChangeAndSubmit: (event: { target: { value: string } }) => Promise<void>;
	onBlur: (newValue?: string | number) => Promise<void>;
	fromTable?: boolean;
	username?: string;
	authUsername?: string;
	activeEditing?: boolean;
	moreHorizon?: JSX.Element;
	charLimit?: number;
}

interface ToastContent {
	type: ToastVariant;
	title: string;
	message: string;
}

/**
 * Note Field Type
 * @param {Object} props for the component as follows
 * @param {Object} color config data
 * @param {FieldInstance} fieldInstance for the current field.
 * @param {FieldTemplate} fieldTemplate for the current fieldInstance
 * @param {String} value for the current fieldInstance
 * @param {Function} handleChange callback from the useField hook for the controlled input.
 * @param {Function} onBlur callback for submission from useField hook.
 * @param {Boolean} fromTable determines if the field instance is in a table or not.
 * @param {ID} expandedField id of the expanded field within a table from the ExpandedContext hook.  This is setup by TableField.
 */
function NoteField(props: NoteFieldProps) {
	const {
		color,
		fieldInstance,
		fieldTemplate,
		value,
		handleChange,
		handleChangeAndSubmit,
		onBlur,
		fromTable,
		username,
		authUsername,
		// activeEditing,
		moreHorizon,
		charLimit,
	} = props;

	const maxLength = charLimit || 390000;
	const ref = useRef<HTMLTextAreaElement>(null);
	const [currentValue, setCurrentValue] = useState(value);
	const { showBoundary } = useErrorBoundary();
	const [openToast, setOpenToast] = useState(false);
	const [lastUpdatedAt, setLastUpdatedAt] = useState<string>(fieldInstance.updatedAt);
	const [toastContent, setToastContent] = useState<ToastContent>({
		type: "info",
		title: fieldTemplate.header,
		message: "",
	});

	// this is to reset the value, now that this component needs to control itself for the undo "ctrl + z" functionality
	useEffect(() => {
		if (fieldInstance.updatedAt > lastUpdatedAt) {
			setCurrentValue(value);
			setLastUpdatedAt(fieldInstance.updatedAt);
		}
	}, [value, fieldInstance.updatedAt, lastUpdatedAt]);

	/**
	 * on change handler to manage cursor position and also check the length of the value against our maximum character limit.
	 * @param e event object from textarea input
	 */
	const handleChangeMade = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const timestamp = new Date().toISOString();
		const prevValue = currentValue;
		setCurrentValue(e.target.value);
		setLastUpdatedAt(timestamp);

		// This is to avoid unnecessary submissions when clearing the field
		if (e.target.value !== prevValue && e.target.value === "") {
			handleClearField(e);
			return;
		}
		if (e.target.value.length >= maxLength) {
			setToastContent({
				type: "destructive",
				title: fieldTemplate.header,
				message: `You have reached the maximum amount of ${maxLength} characters allowed.`,
			});
			setOpenToast(true);
			recordSentry({ error: "You have reached the maximum amount of characters allowed.", msg: "You have reached the maximum characters allowed" });
		} else {
			handleChange && handleChange(e);
		}
	};

	const handleClearField = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		handleChangeAndSubmit(e);
	};

	// The regular handleblur gets the whole event passed in, we can just call it since we have the
	// value already stored in the hook.
	/**
	 * Handle Blur callback, resets the height of the textarea and calls the onBlur props callback, manages focus state
	 */
	function handleBlur() {
		try {
			onBlur();
		} catch (error) {
			recordSentry({ error, msg: "NoteField onBlur Failed" });
			showBoundary(error);
		}
	}
	return (
		<div className={styles.wrapper}>
			<ConfirmationModal
				colorPalette={color}
				details={toastContent.message}
				confirmTitle={toastContent.title}
				open={openToast}
				setOpen={setOpenToast}
				toast={toastContent.type}
				timeout={5000}
				canClose
			/>
			<FieldWrapper colorPalette={color} label={!fromTable ? fieldTemplate.header : undefined} data-test="Field-Header">
				{username && username !== authUsername ? (
					<OccupiedField username={username} />
				) : (
					<FieldActionWrapper>
						<NoteFieldTextArea
							colorPalette={color}
							id="review-text"
							data-fieldtemplateid={fieldTemplate.id}
							onChange={handleChangeMade}
							onBlur={handleBlur}
							placeholder={fieldTemplate.placeholder || fieldTemplate.header}
							ref={ref}
							tabIndex={0}
							value={currentValue}
							moreHorizon={moreHorizon ? moreHorizon : undefined}
							maxLength={charLimit}
							label={fieldTemplate.header}
						/>
					</FieldActionWrapper>
				)}
			</FieldWrapper>
		</div>
	);
}

export default NoteField;
